import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  TextField,
} from '@mui/material';

const ProductDetails = (props) => {
  const { handleContinue, handleBack, products, data, t } = props;
  const [errorDisplay, setErrorDisplay] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);

  // States to manage quantities, porting values, and their errors
  const [quantities, setQuantities] = useState({});
  const [portings, setPortings] = useState({});
  const [errors, setErrors] = useState({});

  // Refs to manage focus on error fields
  const quantityRefs = useRef({});
  const portingRefs = useRef({});

  useEffect(() => {
    if (data.currentStep1) {
      const prevData = new Set(data.currentStep1);
      const filtered = products.filter((product) => prevData.has(product.id));
      setFilteredProducts(filtered);

      // Initialize quantities and portings with default values
      if(data.currentStep2){
        const temp = data.currentStep2
        setQuantities(temp.quantities)
        setPortings(temp.portings)
      }
      else{
        const initialQuantities = {};
        const initialPortings = {};
        filtered.forEach((product) => {
          initialQuantities[product.id] = product.min_quantity || 1;
          initialPortings[product.id] = 0;
        });
        setQuantities(initialQuantities);
        setPortings(initialPortings);
      }
      
    } else {
      setErrorDisplay(t('SomethingWentWrong'));
    }
  }, [data, products]);

  // Handler to validate Quantity field on change and blur
  const handleQuantityChange = (event, product) => {
    const { value } = event.target;
    const numericValue = parseInt(value, 10);
    const min = product.min_quantity || 1;
    const max = 1000;

    // Update quantities state
    setQuantities((prevQuantities) => ({
      ...prevQuantities,
      [product.id]: numericValue,
    }));

    // Validate the input value
    if (isNaN(numericValue) || numericValue < min || numericValue > max) {
      // Set error for the specific product ID
      setErrors((prevErrors) => ({
        ...prevErrors,
        [`quantity_${product.id}`]: `${t('ValueBetween')} ${min} , ${max}.`,
      }));
    } else {
      // Remove error for the specific product ID
      setErrors((prevErrors) => {
        const { [`quantity_${product.id}`]: removedError, ...rest } = prevErrors;
        return rest;
      });

      // Additionally, validate porting if it's greater than the new quantity
      const currentPorting = parseInt(portings[product.id], 10);
      if (!isNaN(currentPorting) && currentPorting > numericValue) {
        setPortings((prevPortings) => ({
          ...prevPortings,
          [product.id]: numericValue, // Adjust porting to new quantity
        }));
        setErrors((prevErrors) => ({
          ...prevErrors,
          [`porting_${product.id}`]: `${t('PortingQuantityExceed')} (${numericValue}).`,
        }));
      } else {
        // Remove porting error if any
        setErrors((prevErrors) => {
          const { [`porting_${product.id}`]: removedError, ...rest } = prevErrors;
          return rest;
        });
      }
    }
  };

  // Handler to validate Porting field on change and blur
  const handlePortingChange = (event, product) => {
    const { value } = event.target;
    const numericValue = parseInt(value, 10);
    const min = 0;
    const max = parseInt(quantities[product.id], 10) || 1;

    // Update portings state
    setPortings((prevPortings) => ({
      ...prevPortings,
      [product.id]: numericValue,
    }));

    // Validate the input value
    if (isNaN(numericValue) || numericValue < min || numericValue > max) {
      // Set error for the specific product ID
      setErrors((prevErrors) => ({
        ...prevErrors,
        [`porting_${product.id}`]: `${t('ValueBetween')} ${min} , ${max}.`,
      }));
    } else {
      // Remove error for the specific product ID
      setErrors((prevErrors) => {
        const { [`porting_${product.id}`]: removedError, ...rest } = prevErrors;
        return rest;
      });
    }
  };

  // Handle form submission with final validation
  const handleSubmit = (e) => {
    e.preventDefault();
    let valid = true;
    const newErrors = {};

    // Iterate over all filtered products to check for errors
    filteredProducts.forEach((product) => {
      const quantityValue = parseInt(quantities[product.id], 10);
      const quantityMin = product.min_quantity || 1;
      const quantityMax = 1000;

      if (isNaN(quantityValue) || quantityValue < quantityMin || quantityValue > quantityMax) {
        valid = false;
        newErrors[`quantity_${product.id}`] = `${t('ValueBetween')} ${quantityMin}, ${quantityMax}.`;
      }

      const portingValue = parseInt(portings[product.id], 10);
      const portingMin = 0;
      const portingMax = quantityValue || 1;

      if (isNaN(portingValue) || portingValue < portingMin || portingValue > portingMax) {
        valid = false;
        newErrors[`porting_${product.id}`] = `${t('ValueBetween')} ${portingMin}, ${portingMax}.`;
      }
    });

    if (!valid) {
      setErrors(newErrors);

      // Find the first error field and focus it
      const firstErrorKey = Object.keys(newErrors)[0];
      if (firstErrorKey) {
        if (firstErrorKey.startsWith('quantity_')) {
          const productId = firstErrorKey.split('_')[1];
          if (quantityRefs.current[productId]) {
            quantityRefs.current[productId].focus();
          }
        } else if (firstErrorKey.startsWith('porting_')) {
          const productId = firstErrorKey.split('_')[1];
          if (portingRefs.current[productId]) {
            portingRefs.current[productId].focus();
          }
        }
      }
    } else {
      handleContinue({
        quantities: quantities,
        portings: portings
      });
    }
  };

  return (
    <Box sx={{ p: 4, width: '65vw' }}>
      {errorDisplay ? (
        <Typography color="error" variant="h6">
          {errorDisplay}
        </Typography>
      ) : (
        <form onSubmit={handleSubmit}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ bgcolor: '#4BABCC' }}>
                <TableRow>
                  <TableCell>
                    <Typography color={'white'} fontWeight="bold">
                      {t('Product')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color={'white'} fontWeight="bold">
                      {t('ProductFamily')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color={'white'} fontWeight="bold">
                      {t('Description')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color={'white'} fontWeight="bold">
                      {t('OrderedQty')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color={'white'} fontWeight="bold">
                      {t('UnitPrice')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color={'white'} fontWeight="bold">
                      {t('Porting')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color={'white'} fontWeight="bold">
                      {t('Subtotal')}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>{product.product_family}</TableCell>
                    <TableCell>{product.description}</TableCell>
                    <TableCell>
                      <TextField
                        id={`quantity-${product.id}`}
                        label="Quantity"
                        type="number"
                        name={`quantity_${product.id}`}
                        value={quantities[product.id]}
                        onChange={(e) => handleQuantityChange(e, product)}
                        onBlur={(e) => handleQuantityChange(e, product)}
                        InputProps={{
                          inputProps: {
                            min: product.min_quantity || 1,
                            max: 1000,
                          },
                        }}
                        variant="outlined"
                        size="small"
                        error={Boolean(errors[`quantity_${product.id}`])}
                        helperText={errors[`quantity_${product.id}`] || ''}
                        fullWidth
                        inputRef={(el) => (quantityRefs.current[product.id] = el)}
                      />
                    </TableCell>
                    <TableCell>{product.currency}{product.list_price}</TableCell>
                    <TableCell>
                      {product.porting_enable && <TextField
                        id={`porting-${product.id}`}
                        label="Porting"
                        type="number"
                        name={`porting_${product.id}`}
                        value={portings[product.id]}
                        onChange={(e) => handlePortingChange(e, product)}
                        onBlur={(e) => handlePortingChange(e, product)}
                        InputProps={{
                          inputProps: {
                            min: 0,
                            max: parseInt(quantities[product.id], 10) || 1,
                          },
                        }}
                        variant="outlined"
                        size="small"
                        error={Boolean(errors[`porting_${product.id}`])}
                        helperText={errors[`porting_${product.id}`] || ''}
                        fullWidth
                        inputRef={(el) => (portingRefs.current[product.id] = el)}
                      />}
                      {!product.porting_enable && <Typography
                        component="span"
                        >NA</Typography>  
                    }
                    </TableCell>
                    <TableCell>
                      <Typography
                        component="span"
                        sx={{
                          color: '#00AEEF', // Blue color
                          fontWeight: 'bold',
                        }}
                      >
                        {product.currency}{((parseInt(quantities[product.id], 10) || 0) * product.list_price).toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Back and Continue Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="text"
              sx={{ color: '#00AEEF' }}
              onClick={handleBack}
            >
              {t('Back')} 
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ backgroundColor: '#00AEEF', color: 'white' }}
            >
              {t('Continue')}
            </Button>
          </Box>
        </form>
      )}
    </Box>
  );
};

export default ProductDetails;
